import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import './Sections.css';
import useSession from '../hooks/useSession';
import useCalendarEvents from '../hooks/useCalendarEvents';
import { eventWhen, fullWhen, plainPreview } from '../lib/eventText';

/* ---------- Hero ---------- */
export const Hero = () => {
  const { user, hasMember } = useSession();
  return (
  <section id="home" className="hero">
    <div className="hero-inner">
      <span className="eyebrow glass">নমস্কার • Welcome</span>
      <h1 className="hero-title">
        Dutchess County <span className="accent">Bhabani</span>
      </h1>
      <p className="hero-sub">
        A Bengali cultural community in the Hudson Valley — celebrating Durga Puja,
        our language, food, music, and the warmth of being together.
      </p>
      <div className="hero-actions">
        {/* /events is members-only, so only offer it to members/admins. */}
        {hasMember && <Link to="/events" className="btn-primary">Upcoming Events</Link>}
        {/* Only shown to signed-out visitors. */}
        {!user && <Link to="/login" className="btn-ghost glass">Become a Member</Link>}
      </div>
      <div className="hero-stats">
        <div className="stat glass">
          <strong>150+</strong>
          <span>Member families</span>
        </div>
        <div className="stat glass">
          <strong>12</strong>
          <span>Years of Puja</span>
        </div>
        <div className="stat glass">
          <strong>20+</strong>
          <span>Events a year</span>
        </div>
      </div>
    </div>
  </section>
  );
};

/* ---------- About ---------- */
export const About = () => (
  <section id="about" className="section">
    <div className="section-head">
      <h2>Who We Are</h2>
      <p className="section-lead">
        Rooted in Bengali tradition, growing together in the Hudson Valley.
      </p>
    </div>
    <div className="about-grid">
      <div className="glass card about-lead-card">
        <p>
          Dutchess County Bhabani brings together Bengali families across the region
          to keep our culture alive far from home. From the beats of the dhak during
          Durga Puja to shared plates of khichuri and mishti, we build friendships
          that span generations.
        </p>
        <p>
          Everyone is welcome — whether you grew up in Kolkata, were born here, or
          simply love Bengali art, food, and community.
        </p>
      </div>
      <div className="values">
        {[
          { icon: '🪷', title: 'Culture', text: 'Puja, poetry, dance, and music that carry our heritage forward.' },
          { icon: '🍲', title: 'Community', text: 'Potlucks, adda, and celebrations that feel like family.' },
          { icon: '📚', title: 'Language', text: 'Bengali classes and storytelling for our next generation.' },
          { icon: '🤝', title: 'Service', text: 'Giving back to the wider Hudson Valley we call home.' },
        ].map((v) => (
          <div className="glass card value" key={v.title}>
            <span className="value-icon">{v.icon}</span>
            <h3>{v.title}</h3>
            <p>{v.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Events ---------- */
// Events come live from a Google Calendar via the backend (`GET /calendar`),
// so they're edited from the calendar app — no code changes needed. The
// endpoint is behind the `member` gate, so the fetch carries the signed-in
// user's ID token (via listCalendarEvents); signed-out visitors are prompted
// to join. Event descriptions may use Markdown, rendered (sanitized) below.
// Date/time + description-preview helpers live in ../lib/eventText.

// Restrict Markdown to a safe inline/block subset (react-markdown already
// blocks raw HTML by default); links open in a new tab, images are limited to
// http(s), and headings are downshifted so a description heading never renders
// as a page-level title inside the card. remark-gfm auto-links bare URLs and
// email addresses (-> mailto:) in the description; this stays safe because
// react-markdown emits React <a> elements (never raw HTML) and sanitizes the
// scheme, so javascript:/data: links can't be forged.
const MD_ALLOWED = [
  'p', 'a', 'em', 'strong', 'ul', 'ol', 'li', 'br', 'code', 'blockquote', 'img',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
];
// Downshift the semantic heading tag (the card title is already an <h3>, so
// description headings sit below it in the outline) while keeping a graduated
// visual scale per Markdown level (.event-desc-h1 … -h6) so `#`/`##`/`###`
// read as a real hierarchy inside the card rather than uniform bold text.
const HEADING_TAG = { h1: 'h4', h2: 'h5', h3: 'h6', h4: 'h6', h5: 'h6', h6: 'h6' };
const makeHeading = (mdTag) => {
  const Tag = HEADING_TAG[mdTag];
  return function Heading({ node: _node, ...props }) {
    return <Tag className={`event-desc-h event-desc-${mdTag}`} {...props} />;
  };
};
const MD_COMPONENTS = {
  a({ node: _node, href, ...props }) {
    // react-markdown supplies the link text as children via {...props} and has
    // already sanitized the scheme (javascript:/data: are stripped by its
    // default urlTransform). http(s) links open in a new tab; mailto:/tel:
    // (from GFM autolinked emails) open in the same context as usual.
    const external = /^https?:\/\//i.test(href || '');
    const tabProps = external ? { target: '_blank', rel: 'noopener noreferrer' } : {};
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    return <a href={href} {...tabProps} {...props} />;
  },
  img({ node: _node, src, alt, ...props }) {
    // Only render http(s) images (blocks data:/javascript: and other schemes).
    if (!/^https?:\/\//i.test(src || '')) return null;
    return <img src={src} alt={alt || ''} loading="lazy" className="event-img" {...props} />;
  },
  h1: makeHeading('h1'),
  h2: makeHeading('h2'),
  h3: makeHeading('h3'),
  h4: makeHeading('h4'),
  h5: makeHeading('h5'),
  h6: makeHeading('h6'),
};

// Public "add this calendar" link for the shared events Google Calendar — lets
// members subscribe so events show up in their own Google Calendar.
const GCAL_SUBSCRIBE_URL =
  'https://calendar.google.com/calendar/u/0?cid=Y182MzQ3M2ZhODgwN2ViOTQ4YWY3NzYwM2JhMWQ3NzM1OTAzMGJjZWM4ZDNjODJhOTYxMTQ1MDM3ODU4YTRmMTlhQGdyb3VwLmNhbGVuZGFyLmdvb2dsZS5jb20';

// "Add this calendar to your Google Calendar" link.
export const AddToCalendarButton = ({ className = 'btn-ghost glass' }) => (
  <a className={className} href={GCAL_SUBSCRIBE_URL} target="_blank" rel="noopener noreferrer">
    Add to my Google Calendar
  </a>
);

// Full event details in a modal dialog. Closes on Escape, backdrop click, or the
// close button; locks body scroll and restores focus to the opener on close.
export function EventModal({ event, onClose }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const opener = document.activeElement;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    if (dialogRef.current) dialogRef.current.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [onClose]);

  const { month, day } = eventWhen(event);
  const when = fullWhen(event);
  return (
    <div className="event-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="event-modal"
        role="dialog"
        aria-modal="true"
        aria-label={event.title}
        tabIndex={-1}
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="event-modal-close" onClick={onClose} aria-label="Close details">
          ×
        </button>
        <div className="event-modal-head">
          <div className="event-date">
            <span className="event-month">{month}</span>
            <span className="event-day">{day}</span>
          </div>
          <div className="event-modal-headtext">
            <h3 className="event-modal-title">{event.title}</h3>
            {when && <p className="event-modal-when">{when}</p>}
            {event.location && <p className="event-modal-loc">{event.location}</p>}
          </div>
        </div>
        {event.description && (
          <div className="event-desc event-modal-desc">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              allowedElements={MD_ALLOWED}
              unwrapDisallowed
              components={MD_COMPONENTS}
            >
              {event.description}
            </ReactMarkdown>
          </div>
        )}
        {event.htmlLink && (
          <a
            className="event-modal-link"
            href={event.htmlLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            View on Google Calendar →
          </a>
        )}
      </div>
    </div>
  );
}

// A single clickable event card (opens the details modal via onOpen). Shared by
// the home-page Events section and the full Events page.
export const EventCard = ({ event, onOpen }) => {
  const { month, day, time } = eventWhen(event);
  const meta = [time, event.location].filter(Boolean).join(' · ');
  return (
    <article
      className="glass card event event-clickable"
      role="button"
      tabIndex={0}
      aria-label={`${event.title} — view details`}
      onClick={() => onOpen(event)}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          onOpen(event);
        }
      }}
    >
      <div className="event-date">
        <span className="event-month">{month}</span>
        <span className="event-day">{day}</span>
      </div>
      <div className="event-body">
        {meta && <span className="event-tag">{meta}</span>}
        <h3>{event.title}</h3>
        {event.description && <p className="event-desc-preview">{plainPreview(event.description)}</p>}
        <span className="event-details-hint">View details →</span>
      </div>
    </article>
  );
};

// How many upcoming events the home page previews before "View all events".
const HOME_EVENTS = 4;

// Status notes + a grid of event cards + the details modal. `events` are the
// ones to display (the caller slices/pages); `status` drives the notes. Shared
// by the home Events section and the /events page.
export const EventsGrid = ({ events, status }) => {
  const [selected, setSelected] = useState(null);
  return (
    <>
      {status === 'loading' && <p className="events-note">Loading events…</p>}
      {status === 'signedOut' && (
        <p className="events-note">
          Upcoming events are for members. <Link to="/login">Sign in or join</Link> to see what’s coming up.
        </p>
      )}
      {status === 'error' && (
        <p className="events-note">Events are unavailable right now — please check back soon.</p>
      )}
      {status === 'ready' && events.length === 0 && (
        <p className="events-note">No upcoming events scheduled yet — check back soon!</p>
      )}

      {events.length > 0 && (
        <div className="events-grid">
          {events.map((e) => (
            <EventCard key={e.id} event={e} onOpen={setSelected} />
          ))}
        </div>
      )}

      {selected && <EventModal event={selected} onClose={() => setSelected(null)} />}
    </>
  );
};

export const Events = () => {
  // Fetch one more than we preview so we know whether "View all" is warranted.
  const { events, status } = useCalendarEvents({ max: HOME_EVENTS + 1 });
  return (
    <section id="events" className="section">
      <div className="section-head">
        <h2>Upcoming Events</h2>
        <p className="section-lead">Mark your calendar — there's always something to celebrate.</p>
      </div>

      <EventsGrid events={events.slice(0, HOME_EVENTS)} status={status} />

      {status === 'ready' && events.length > HOME_EVENTS && (
        <div className="events-cta">
          <Link className="btn-primary" to="/events">View all events</Link>
        </div>
      )}
    </section>
  );
};

/* ---------- Gallery ---------- */
const gallery = [
  { emoji: '🪘', label: 'Dhak & Dhunuchi' },
  { emoji: '🌺', label: 'Pushpanjali' },
  { emoji: '🍛', label: 'Community Bhog' },
  { emoji: '💃', label: 'Cultural Night' },
  { emoji: '🎨', label: 'Alpona Art' },
  { emoji: '🎇', label: 'Diwali Lights' },
];

export const Gallery = () => (
  <section id="gallery" className="section">
    <div className="section-head">
      <h2>Moments Together</h2>
      <p className="section-lead">A glimpse of the joy we share, season after season.</p>
    </div>
    <div className="gallery-grid">
      {gallery.map((g, i) => (
        <div className={`glass gallery-item item-${i}`} key={g.label}>
          <span className="gallery-emoji">{g.emoji}</span>
          <span className="gallery-label">{g.label}</span>
        </div>
      ))}
    </div>
  </section>
);

/* ---------- Get Involved ---------- */
export const GetInvolved = () => (
  <section id="get-involved" className="section">
    <div className="glass cta-card">
      <div className="cta-text">
        <h2>Become Part of the Family</h2>
        <p>
          Join Dutchess County Bhabani to help organize festivals, enroll your kids in
          Bengali classes, volunteer, and stay connected all year round.
        </p>
      </div>
      <div className="cta-actions">
        <Link to="/login" className="btn-primary">Sign in or create an account</Link>
      </div>
    </div>
  </section>
);
